import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Shield, Mail, Lock, Eye, EyeOff, Loader, CheckCircle, AlertCircle, RefreshCw, Fingerprint } from 'lucide-react'
import RequestAccessModal from '../components/RequestAccessModal'
import NdaClickwrap from '../components/NdaClickwrap'
import { useTenant } from '../contexts/TenantContext'
import { startAuthentication } from '@simplewebauthn/browser'
import { setAuthToken, removeAuthToken } from '../utils/auth'

const LoginPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentTenant, loading: tenantLoading } = useTenant()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    verificationCode: ''
  })

  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [requiresVerification, setRequiresVerification] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [resendingCode, setResendingCode] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [useCodeOnly, setUseCodeOnly] = useState(true)
  const [codeSent, setCodeSent] = useState(false)
  const [companyName, setCompanyName] = useState(currentTenant?.name || '')
  const [passkeySupported, setPasskeySupported] = useState(false)
  const [showPasskeyLogin, setShowPasskeyLogin] = useState(false)

  // NDA modal state
  const [showNdaModal, setShowNdaModal] = useState(false)
  const [ndaSettings, setNdaSettings] = useState<any>(null)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)

  // Google Sign-In initialization guards (persist across re-renders)
  const hasInitializedRef = useRef(false)
  const hasPromptedRef = useRef(false)
  const [oneTapBlocked, setOneTapBlocked] = useState(false)
  const googleButtonRef = useRef<HTMLDivElement>(null) // Persist button element across renders

  // Auto-redirect if already logged in
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('auth-token')
      if (!token) return

      try {
        // Validate the token with backend
        const response = await fetch('/api/auth/validate', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          // User is already logged in, redirect them
          const returnUrl = sessionStorage.getItem('returnUrl')
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl')
            navigate(returnUrl, { replace: true })
          } else {
            // On tenant sites, redirect to evidence library instead of admin
            // @ts-ignore - __TENANT_CONFIG__ is injected at build time
            const isTenantSite = typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__?.tenantId
            navigate(isTenantSite ? '/evidence' : '/admin', { replace: true })
          }
        } else {
          // Token is invalid, remove it
          localStorage.removeItem('auth-token')
        }
      } catch (err) {
        console.error('Auth validation error:', err)
        // On error, just stay on login page
      }
    }

    checkExistingAuth()
  }, [navigate])

  // Check for error message from URL params (e.g., from OAuth failures) or session timeout
  useEffect(() => {
    // Check if WebAuthn is supported
    if (window.PublicKeyCredential) {
      setPasskeySupported(true)
    }

    // Check for session timeout
    const sessionTimedOut = sessionStorage.getItem('sessionTimedOut')
    if (sessionTimedOut === 'true') {
      setError('Your session has expired. Please click "Sign in with Google" below to continue.')
      sessionStorage.removeItem('sessionTimedOut')
    }

    // Check for URL error params
    const urlError = searchParams.get('error')
    if (urlError) {
      setError(urlError)
    }
  }, [searchParams])

  // Fetch company name from settings
  useEffect(() => {
    let isMounted = true

    const fetchCompanyName = async () => {
      const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id
      if (!tenantId) return

      try {
        const version = sessionStorage.getItem('content-version') || 'live'
        const response = await fetch(`/api/settings-db`, {
          headers: {
            'X-Tenant-ID': tenantId,
            'X-Content-Version': version
          }
        })

        if (response.ok && isMounted) {
          const data = await response.json()
          if (data.companyName) {
            setCompanyName(data.companyName)
          }
        }
      } catch (err) {
        console.error('Failed to fetch company name:', err)
      }
    }

    if (!tenantLoading) {
      fetchCompanyName()
    }

    return () => {
      isMounted = false
    }
  }, [currentTenant, tenantLoading])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          verificationCode: formData.verificationCode || undefined
        })
      })

      const data = await response.json()

      if (response.ok) {
        if (data.requiresVerification) {
          setRequiresVerification(true)
          setSuccess('Please enter your verification code to complete login')
        } else {
          setSuccess('Login successful! Redirecting...')
          setAuthToken(data.token)

          // Check for returnUrl to redirect back to protected page
          const returnUrl = sessionStorage.getItem('returnUrl')
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl')
            setTimeout(() => navigate(returnUrl), 1500)
          } else {
            setTimeout(() => navigate('/evidence'), 1500)
          }
        }
      } else {
        setError(data.message || 'Login failed')
      }
    } catch (error: any) {
      console.error('Login error:', error, {
        message: error?.message,
        stack: error?.stack,
        email: formData.email
      })
      setError('Network error. Please try again.')
    }

    setIsLoading(false)
  }

  const handleRequestCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    if (!formData.email) {
      setError('Please enter your email first')
      return
    }

    // Wait for tenant context to finish loading
    if (tenantLoading) {
      setError('Loading tenant configuration...')
      return
    }

    setIsLoading(true)
    setError('')

    // Get tenant ID from sessionStorage (set by branch deploy config)
    const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id

    console.log('🔍 Checking tenant ID:', {
      sessionStorage: sessionStorage.getItem('tenant-id'),
      currentTenant: currentTenant?.id,
      currentTenantObject: currentTenant,
      finalTenantId: tenantId,
      tenantIdType: typeof tenantId
    })

    if (!tenantId || typeof tenantId !== 'string' || tenantId.trim() === '') {
      // No tenant ID found - show registration modal
      console.log('❌ No valid tenant ID found, showing registration modal')
      setShowRequestModal(true)
      setIsLoading(false)
      return
    }

    console.log('✅ Sending request with tenant ID:', tenantId)

    try {
      const requestBody = {
        email: formData.email,
        tenantId: tenantId
      }
      console.log('📤 Request body:', requestBody)

      const response = await fetch('/api/auth/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })

      if (response.ok) {
        setCodeSent(true)
        setSuccess('Verification code sent to your email')
      } else {
        const data = await response.json()
        if (data.requiresAccessRequest) {
          // User doesn't exist - show request access modal
          setShowRequestModal(true)
          setError('')
        } else {
          setError(data.message || 'Failed to send code')
        }
      }
    } catch (error) {
      setError('Network error. Please try again.')
    }

    setIsLoading(false)
  }

  // Check NDA requirement after successful login
  const checkNdaRequirement = async (email: string, destinationUrl: string) => {
    const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id

    if (!tenantId) {
      // No tenant context, skip NDA check
      navigate(destinationUrl)
      return
    }

    try {
      console.log('[LoginPage] Checking NDA requirement for:', email)
      const response = await fetch('/api/auth/nda-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          userEmail: email,
          userCompany: '' // Will be extracted from email domain
        })
      })

      if (!response.ok) {
        console.error('[LoginPage] NDA check failed:', response.status)
        // On error, allow access anyway (fail open for login flow)
        navigate(destinationUrl)
        return
      }

      const ndaStatus = await response.json()
      console.log('[LoginPage] NDA status:', ndaStatus)

      if (ndaStatus.status === 'required' && ndaStatus.clickwrapAvailable) {
        // Show NDA modal
        setNdaSettings(ndaStatus.clickwrapSettings)
        setPendingNavigation(destinationUrl)
        setShowNdaModal(true)
      } else if (ndaStatus.status === 'denied') {
        // NDA required but not configured - show error and navigate with limited access
        setError('NDA acceptance required. You will have limited access until NDA is accepted.')
        setTimeout(() => navigate(destinationUrl), 2000)
      } else {
        // NDA not required or already accepted
        console.log('[LoginPage] NDA check passed:', ndaStatus.message)
        navigate(destinationUrl)
      }
    } catch (err) {
      console.error('[LoginPage] NDA check error:', err)
      // On error, allow access anyway (fail open for login flow)
      navigate(destinationUrl)
    }
  }

  const handleNdaAccept = () => {
    setShowNdaModal(false)
    if (pendingNavigation) {
      navigate(pendingNavigation)
      setPendingNavigation(null)
    }
  }

  const handleNdaReject = () => {
    setShowNdaModal(false)
    setPendingNavigation(null)

    // Log out the user completely
    localStorage.removeItem('auth-token')

    setError('NDA acceptance is required to access this Trust Center. You have been logged out. Please contact the administrator if you believe this is an error.')

    // Do NOT navigate - keep them on login page
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.verificationCode) {
      setError('Please enter your email and verification code')
      return
    }

    // Get tenant ID
    const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id

    if (!tenantId) {
      setError('Tenant configuration not found. Please refresh the page.')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          code: formData.verificationCode,
          tenantId
        })
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess('Login successful!')
        setAuthToken(data.token)
        console.log('✅ Token stored in localStorage:', data.token.substring(0, 20) + '...')

        // Determine destination URL
        const returnUrl = sessionStorage.getItem('returnUrl')
        const destinationUrl = returnUrl || '/evidence'
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl')
        }

        // Check NDA requirement before navigating
        console.log('🔍 Checking NDA requirement before navigation...')
        setTimeout(() => checkNdaRequirement(formData.email, destinationUrl), 500)
      } else {
        setError(data.message || 'Invalid verification code')
      }
    } catch (error) {
      setError('Network error. Please try again.')
    }

    setIsLoading(false)
  }

  const handleResendCode = async () => {
    if (!formData.email) {
      setError('Please enter your email first')
      return
    }

    setResendingCode(true)
    setError('')

    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      })

      if (response.ok) {
        setSuccess('New verification code sent to your email')
      } else {
        const data = await response.json()
        setError(data.message || 'Failed to resend code')
      }
    } catch (error) {
      setError('Network error. Please try again.')
    }

    setResendingCode(false)
  }

  const handleGoogleSSO = () => {
    // Clear any existing errors
    setError('')

    // GIS handles the sign-in flow automatically via the button
    // This function is now just for the manual trigger if needed
    const googleButton = document.getElementById('google-signin-button')
    if (googleButton) {
      googleButton.click()
    }
  }

  // Initialize Google Identity Services
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      // Check if GIS library is loaded
      if (typeof window.google === 'undefined') {
        return false
      }

      // Global flag to prevent multiple initializations across component mounts
      // @ts-ignore
      if (window.__GOOGLE_SIGNIN_INITIALIZED__) {
        console.log('🔐 Google Sign-In already initialized globally, skipping...')
        return true
      }

      // Prevent multiple initializations (persist across React Strict Mode double-runs)
      if (hasInitializedRef.current) {
        console.log('🔐 Google Sign-In already initialized, skipping...')
        return true
      }

      try {
        // Get Google Client ID from environment
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

        // In production, REQUIRE environment variable to be set
        const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'

        if (!clientId && !isDevelopment) {
          console.error('❌ VITE_GOOGLE_CLIENT_ID environment variable not configured')
          setError('Google Sign-In is not configured. Please contact your administrator.')
          return false
        }

        // Use hardcoded fallback ONLY in development
        const finalClientId = clientId || '406298590928-2p9o3mjhfj6e8jb3rjd9lf44iivtfhqm.apps.googleusercontent.com'

        if (!clientId && isDevelopment) {
          console.warn('⚠️ Using development Google Client ID. Configure VITE_GOOGLE_CLIENT_ID for production.')
        }

        console.log('🔐 Initializing Google Identity Services...')

        // Initialize GIS - standard config that works everywhere
        window.google.accounts.id.initialize({
          client_id: finalClientId,
          callback: handleGoogleCredentialResponse,
          auto_select: true,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: true
        })

        // Render the Sign In with Google button using ref (persists across React renders)
        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: 'outline',
            size: 'large',
            width: 350,
            text: 'continue_with',
            shape: 'rectangular',
            logo_alignment: 'left'
          })
          console.log('✅ Google Sign-In button rendered')
        }

        hasInitializedRef.current = true
        // @ts-ignore
        window.__GOOGLE_SIGNIN_INITIALIZED__ = true

        // Show One Tap ONCE per page load
        if (!hasPromptedRef.current) {
          hasPromptedRef.current = true
          setTimeout(() => {
            console.log('🎯 Attempting to show One Tap...')
            window.google.accounts.id.prompt((notification: any) => {
              if (notification.isNotDisplayed()) {
                console.warn('⚠️ One Tap not displayed:', notification.getNotDisplayedReason())
                setOneTapBlocked(true)
              } else if (notification.isSkippedMoment()) {
                console.log('⏭️ One Tap skipped:', notification.getSkippedReason())
                setOneTapBlocked(true)
              } else if (notification.isDismissedMoment()) {
                console.log('❌ One Tap dismissed:', notification.getDismissedReason())
              } else {
                console.log('✅ One Tap displayed successfully')
                setOneTapBlocked(false)
              }
            })
          }, 500) // Longer delay to ensure DOM is stable
        }

        return true // Successfully initialized
      } catch (err) {
        console.error('❌ Failed to initialize Google Sign-In:', err)
        return false
      }
    }

    // Try to initialize immediately
    if (initializeGoogleSignIn()) {
      return
    }

    // If SDK not loaded yet, set up retry mechanism
    let retryCount = 0
    const maxRetries = 10
    const retryInterval = 500 // ms

    const retryTimer = setInterval(() => {
      retryCount++

      if (initializeGoogleSignIn()) {
        clearInterval(retryTimer)
        return
      }

      if (retryCount >= maxRetries) {
        console.error('Google Identity Services SDK failed to load after', maxRetries, 'attempts')
        clearInterval(retryTimer)
      }
    }, retryInterval)

    // Cleanup interval on component unmount
    return () => {
      clearInterval(retryTimer)

      // DON'T call google.accounts.id.cancel() - it aborts active login flows!
      // In React Strict Mode (development), this cleanup runs between renders
      // and cancels legitimate auth attempts, causing "request has been aborted" errors.
      // The Google SDK handles its own cleanup, we don't need to interfere.
    }
  }, [])

  const handleGoogleCredentialResponse = async (response: any) => {
    try {
      setIsLoading(true)
      setError('')
      setSuccess('Verifying with Google...')

      // Send the credential (ID token) to our backend for verification
      const verifyResponse = await fetch('/api/auth/google/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: response.credential // This is the JWT ID token from Google
        })
      })

      const data = await verifyResponse.json()

      if (verifyResponse.ok) {
        setSuccess('Login successful! Redirecting...')
        setAuthToken(data.token)

        // Check for returnUrl to redirect back to protected page
        const returnUrl = sessionStorage.getItem('returnUrl')
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl')
          setTimeout(() => navigate(returnUrl), 500)
        } else {
          // On tenant sites, redirect to evidence library instead of admin
          // @ts-ignore - __TENANT_CONFIG__ is injected at build time
          const isTenantSite = typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__?.tenantId
          setTimeout(() => navigate(isTenantSite ? '/evidence' : '/admin'), 500)
        }
      } else {
        setError(data.error || 'Google sign-in failed')
      }
    } catch (err: any) {
      console.error('Google sign-in error:', err)
      setError(err.message || 'Failed to sign in with Google')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasskeyLogin = async () => {
    if (!formData.email) {
      setError('Please enter your email address')
      return
    }

    // Get tenant ID
    const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id

    if (!tenantId) {
      setError('Tenant configuration not found. Please refresh the page.')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Get authentication options from server
      const optionsResponse = await fetch('/api/auth/passkey/login/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, tenantId })
      })

      if (!optionsResponse.ok) {
        const data = await optionsResponse.json()
        throw new Error(data.error || 'Failed to get authentication options')
      }

      const options = await optionsResponse.json()

      // Trigger device authentication (TouchID, FaceID, etc.)
      const credential = await startAuthentication(options)

      // Verify authentication with server
      const verifyResponse = await fetch('/api/auth/passkey/login/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, credential, tenantId })
      })

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json()
        throw new Error(data.error || 'Authentication failed')
      }

      const data = await verifyResponse.json()

      if (data.success) {
        setSuccess('Login successful! Redirecting...')
        setAuthToken(data.token)

        // Check for returnUrl to redirect back to protected page
        const returnUrl = sessionStorage.getItem('returnUrl')
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl')
          setTimeout(() => navigate(returnUrl), 500)
        } else {
          setTimeout(() => navigate('/evidence'), 500)
        }
      } else {
        throw new Error('Authentication failed')
      }

    } catch (err: any) {
      console.error('Passkey login error:', err)
      setError(err.message || 'Failed to authenticate with passkey')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-netlify-900 via-netlify-700 to-netlify-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-netlify-500 to-netlify-600 rounded-full shadow-lg">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {(() => {
              console.log('🏢 LoginPage companyName:', companyName)
              console.log('🏢 currentTenant:', currentTenant)

              if (!companyName) return 'Trust Center'

              // Remove duplicate "Trust Center Trust Center" if it exists
              const cleanName = companyName.replace(/Trust Center\s+Trust Center/gi, 'Trust Center').trim()
              console.log('🧹 Cleaned name:', cleanName)

              // If it already has "Trust Center" in it, use as-is
              if (cleanName.toLowerCase().includes('trust center')) {
                console.log('✅ Using cleaned name as-is:', cleanName)
                return cleanName
              }

              // Otherwise show company name and "Trust Center" on separate lines
              return (
                <>
                  {cleanName}
                  <br />
                  Trust Center
                </>
              )
            })()}
          </h1>
          <p className="text-netlify-200">Secure access to compliance documentation</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* Login Method Toggle */}
          <div className="mb-6 flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => {
                setUseCodeOnly(true)
                setCodeSent(false)
                setError('')
                setSuccess('')
              }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                useCodeOnly
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Verify with Code
            </button>
            <button
              type="button"
              onClick={() => {
                setUseCodeOnly(false)
                setCodeSent(false)
                setError('')
                setSuccess('')
              }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                !useCodeOnly
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Password Login
            </button>
          </div>

          <form onSubmit={useCodeOnly && !codeSent ? handleRequestCode : useCodeOnly ? handleVerifyCode : handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="your.email@company.com"
                disabled={useCodeOnly && codeSent}
              />
            </div>

            {!useCodeOnly && (
              <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-1" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            )}

            {useCodeOnly && codeSent && (
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Verification Code
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    id="verificationCode"
                    name="verificationCode"
                    value={formData.verificationCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => handleRequestCode()}
                    disabled={resendingCode}
                    className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {resendingCode ? (
                      <Loader className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <RefreshCw className="w-3 h-3 mr-1" />
                    )}
                    Resend Code
                  </button>
                </div>
              </div>
            )}

            {!useCodeOnly && requiresVerification && (
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Verification Code
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    id="verificationCode"
                    name="verificationCode"
                    value={formData.verificationCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendingCode}
                    className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {resendingCode ? (
                      <Loader className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <RefreshCw className="w-3 h-3 mr-1" />
                    )}
                    {resendingCode ? 'Sending...' : 'Resend Code'}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="h-4 w-4 text-red-600 mr-2" />
                <span className="text-sm text-red-600">{error}</span>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm text-green-600">{success}</span>
              </div>
            )}

            {tenantLoading && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                <Loader className="h-4 w-4 text-blue-600 mr-2 animate-spin" />
                <span className="text-sm text-blue-600">Loading tenant configuration...</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || tenantLoading}
              className="w-full bg-netlify-600 text-white py-3 px-4 rounded-lg hover:bg-netlify-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-md"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  {useCodeOnly && !codeSent ? 'Sending Code...' : useCodeOnly ? 'Verifying...' : requiresVerification ? 'Verifying...' : 'Signing In...'}
                </>
              ) : (
                useCodeOnly && !codeSent ? 'Send Verification Code' : useCodeOnly ? 'Verify & Sign In' : requiresVerification ? 'Verify & Sign In' : 'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Passkey Login Button */}
          {passkeySupported && formData.email && (
            <>
              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center transition-colors shadow-md"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-5 h-5 mr-2" />
                    Sign in with Passkey (TouchID/FaceID)
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Fast, secure sign-in with your device's biometrics
              </p>
            </>
          )}

          {/* Prominent message when One Tap is blocked */}
          {oneTapBlocked && (
            <div className="mt-3 mb-4 p-4 bg-blue-50 border-2 border-blue-500 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 text-center mb-2">
                👇 Click the button below to sign in
              </p>
              <p className="text-xs text-blue-700 text-center">
                Automatic sign-in is temporarily unavailable. Please use the manual button.
              </p>
            </div>
          )}

          {/* Google Identity Services Button - Rendered by GIS SDK */}
          <div
            ref={googleButtonRef}
            className="flex justify-center mt-3"
            style={{ minHeight: '44px' }}
          />

          {/* SSO Notice */}
          <p className="text-xs text-gray-500 text-center mt-2">
            Powered by Google Identity Services with FedCM
          </p>

          {/* Help Text */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-gray-600">
              Don't have access? <br />
              <button
                onClick={() => setShowRequestModal(true)}
                className="text-blue-600 hover:text-blue-700 underline bg-transparent border-none cursor-pointer"
              >
                Request access from the Trust Center
              </button>
            </p>
            <p className="text-sm text-gray-600">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 underline bg-transparent border-none cursor-pointer"
              >
                ← Back to Trust Center
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Request Access Modal */}
      <RequestAccessModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
      />

      {/* NDA Modal - shown after successful login if NDA required */}
      {showNdaModal && ndaSettings && (
        <NdaClickwrap
          tenantId={sessionStorage.getItem('tenant-id') || currentTenant?.id || ''}
          userEmail={formData.email}
          userCompany=""
          documentId={null}
          ndaTitle={ndaSettings.title}
          ndaText={ndaSettings.text}
          ndaVersion={ndaSettings.version}
          onAccept={handleNdaAccept}
          onReject={handleNdaReject}
        />
      )}
    </div>
  )
}

export default LoginPage